import { useMemo } from 'react';
import { useStore } from '../../store/useStore';
import {
  coreTagIds,
  plannedMinutesByCore,
  recordsById,
  stuckByArea,
  weeksNewestFirst,
  whereWeeksGo,
} from '../../engine/insights';
import { weeklyInsightsEnabled } from '../../native/insightsGate';
import { FocusPicker } from './FocusPicker';

/**
 * "Where your weeks go" (Weekly Insights feature 4, spec §5) — a Mastery
 * section. Last 8 bound weeks as one bar each (Core / Not mine / Other,
 * every bar labelled with its counts and summing to the Mastery record),
 * the areas carrying the most stuck work right now, and planned minutes
 * per ★ tag this week ("planned", never "spent"). Pure, on-device, web only.
 */
export function WhereWeeksGo() {
  const boundWeeks = useStore((s) => s.boundWeeks);
  const tags = useStore((s) => s.tags);
  const tasks = useStore((s) => s.tasks);
  const weekHistory = useStore((s) => s.weekHistory);

  const data = useMemo(() => {
    const coreIds = coreTagIds(tags);
    const open = Object.values(tasks).filter((t) => !t.done);
    return {
      weeks: whereWeeksGo(weeksNewestFirst(boundWeeks), coreIds, recordsById(weekHistory)),
      stuck: stuckByArea(open, tags).slice(0, 5),
      planned: plannedMinutesByCore(open, tags),
      coreCount: coreIds.size,
    };
  }, [boundWeeks, tags, tasks, weekHistory]);

  if (!weeklyInsightsEnabled()) return null;

  const max = Math.max(1, ...data.weeks.map((w) => w.core + w.notMine + w.other));

  return (
    <section aria-label="Where your weeks go" className="space-y-4">
      <div className="space-y-1">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Where your weeks go
        </h4>
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          What you finished each week: <b className="text-brand-600 dark:text-brand-400">focus</b> (★ tags) ·{' '}
          <b className="text-amber-600 dark:text-amber-400">not mine</b> (Zone 3 or handed off) ·{' '}
          <b className="text-slate-500">other</b>.
        </p>
      </div>

      {data.weeks.length === 0 ? (
        <p className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 text-center text-xs italic text-slate-500">
          Close a week to see this.
        </p>
      ) : (
        <div className="space-y-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
          {data.weeks.map((w) => {
            const total = w.core + w.notMine + w.other;
            const pct = (n: number) => `${(n / max) * 100}%`;
            return (
              <div key={w.weekId} className="space-y-1">
                <div className="flex justify-between text-[11px]">
                  <span className="font-bold text-slate-700 dark:text-slate-200">{w.label}</span>
                  <span className="text-slate-500 dark:text-slate-400">
                    {w.core} focus · {w.notMine} not mine · {w.other} other · {total} finished
                  </span>
                </div>
                <div
                  className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-950"
                  role="img"
                  aria-label={`${w.label}: ${w.core} focus, ${w.notMine} not mine, ${w.other} other`}
                >
                  <div className="h-full bg-brand-500" style={{ width: pct(w.core) }} />
                  <div className="h-full bg-amber-400" style={{ width: pct(w.notMine) }} />
                  <div className="h-full bg-slate-400 dark:bg-slate-600" style={{ width: pct(w.other) }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data.stuck.length > 0 && (
        <div className="space-y-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
          <h5 className="text-xs font-bold text-slate-900 dark:text-white">Stuck by area</h5>
          <ul className="space-y-1">
            {data.stuck.map((a) => (
              <li key={a.tagId} className="flex justify-between text-[11px] text-slate-700 dark:text-slate-300">
                <span className="font-semibold">{a.name}</span>
                <span className="text-slate-500 dark:text-slate-400">
                  {a.tasks} open task{a.tasks === 1 ? '' : 's'} · {a.rollovers} week{a.rollovers === 1 ? '' : 's'} rolled between them
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
        <h5 className="text-xs font-bold text-slate-900 dark:text-white">Planned this week, per ★ focus project</h5>
        <FocusPicker />
        {data.coreCount === 0 ? (
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Star the projects you’re focusing on (★ next to any tag — open a task, then Edit tags) and this fills in.
          </p>
        ) : data.planned.length === 0 ? (
          <p className="text-[11px] text-slate-500 dark:text-slate-400">No open steps on your ★ focus projects yet.</p>
        ) : (
          <ul className="space-y-1">
            {data.planned.map((p) => (
              <li key={p.tagId} className="flex justify-between text-[11px] text-slate-700 dark:text-slate-300">
                <span className="font-semibold">★ {p.name}</span>
                <span className="text-slate-500 dark:text-slate-400">~{p.minutes} min planned</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
